import { forwardRef, useId, useRef, type KeyboardEvent } from "react";
import classNames from "classnames";
import Icon from "@/components/Icon";
import { hasOpenOverlay } from "@/hooks/useOverlayDismiss";
import { useTableNoDataEmpty } from "@/components/Table/TableNoDataEmptyContext";
import { PAGE_SEARCH_ATTR } from "./pageSearch";

export { PAGE_SEARCH_ATTR } from "./pageSearch";

interface SearchInputProps {
  label?: string;
  /** Accessible name for the text field when a visible `label` is not shown. */
  ariaLabel?: string;
  placeholder: string;
  query: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  showManageSearch?: boolean;
  onManageSearch?: () => void;
  /**
   * Persistent hint under the field (Select/TextField supporting-text style).
   * Sentence case. Use for truncation or filter hints, not the placeholder.
   */
  supportingText?: string;
  /** Extra `aria-describedby` id (for example a hint that is not rendered under the field). */
  describedBy?: string;
  /**
   * When true (default), marks this field as a page-search target for the `/` shortcut
   * and handles Enter blur / Escape cancel.
   */
  pageSearchShortcut?: boolean;
  /** Extra key handling that runs before page-search Enter/Escape. GlobalSearch uses a document capture listener instead of this prop. */
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  /** When set, the input is a combobox that owns a listbox (`aria-controls`). */
  listboxId?: string;
  /** When `listboxId` is set, whether the popup is expanded. Defaults to true. */
  listboxExpanded?: boolean;
  /** `aria-activedescendant` option id while `listboxId` is set and expanded. */
  activeOptionId?: string;
  /** Disables the field and omits it from the `/` page-search shortcut. True no-data also disables via TableNoDataEmptyContext so wrappers cannot drop the flag. */
  disabled?: boolean;
}

export default forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  {
    label,
    ariaLabel,
    placeholder,
    query,
    onChange,
    onClear,
    showManageSearch = false,
    onManageSearch,
    supportingText,
    describedBy,
    pageSearchShortcut = true,
    onKeyDown,
    listboxId,
    listboxExpanded = true,
    activeOptionId,
    disabled: disabledProp = false
  }: SearchInputProps,
  ref
) {
  const isNoDataEmpty = useTableNoDataEmpty();
  const disabled = disabledProp || isNoDataEmpty;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const supportingId = useId();
  const describedByIds = [describedBy, supportingText ? supportingId : undefined].filter(Boolean).join(" ");

  const setRefs = (node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || !pageSearchShortcut) return;

    if (event.key === "Enter") {
      event.preventDefault();
      inputRef.current?.blur();
      return;
    }

    if (event.key === "Escape") {
      // Let the overlay dismiss stack own Escape while a Dialog / SideSheet /
      // BottomDrawer is open; preventDefault would block that dismiss path.
      if (hasOpenOverlay()) return;

      event.preventDefault();
      if (query) {
        onChange("");
        onClear?.();
      }
      inputRef.current?.blur();
    }
  };

  return (
    <div>
      {label && <label className="block ml-1 mb-4 text-sys-brand-on-surface-variant">{label}</label>}
      <div className={classNames("relative w-full", { "opacity-[0.38]": disabled })}>
        <Icon name="search" className="absolute left-5 top-1/2 transform -translate-y-1/2" />
        <input
          name="search"
          type="text"
          autoComplete="off"
          ref={setRefs}
          value={query}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-describedby={describedByIds || undefined}
          {...(listboxId
            ? {
                role: "combobox" as const,
                "aria-expanded": listboxExpanded,
                "aria-controls": listboxExpanded ? listboxId : undefined,
                "aria-autocomplete": "list" as const,
                "aria-activedescendant": listboxExpanded ? activeOptionId : undefined
              }
            : {})}
          {...(pageSearchShortcut && !disabled ? { [PAGE_SEARCH_ATTR]: "" } : {})}
          onFocus={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            onChange(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={classNames(
            "pl-14 w-full h-[56px] placeholder-sys-brand-on-surface-variant outline-none",
            disabled ? "cursor-not-allowed" : "cursor-pointer",
            "bg-extended-pgasilver-brand-color rounded-[8px]",
            {
              "pr-24": showManageSearch,
              "pr-14": !showManageSearch && query,
              "pr-10": !showManageSearch && !query
            }
          )}
          data-testid="search-input"
        />
        {query && !disabled && (
          <button
            onClick={() => {
              onChange("");
              onClear?.();
            }}
            className={classNames("absolute h-[24px] w-[24px] top-1/2 transform -translate-y-1/2 cursor-pointer", {
              "right-14": showManageSearch,
              "right-5": !showManageSearch
            })}
            aria-label="Clear search query"
            data-testid="clear-search"
          >
            <Icon name="close" />
          </button>
        )}
        {showManageSearch && !disabled && (
          <button
            onClick={onManageSearch}
            className="absolute h-[24px] w-[24px] right-5 top-1/2 transform -translate-y-1/2 cursor-pointer"
            aria-label="Manage search"
            data-testid="manage-search"
          >
            <Icon name="tune" />
          </button>
        )}
      </div>
      {supportingText ? (
        <p
          id={supportingId}
          className="mt-1 ml-3 text-body-small text-sys-brand-on-surface-variant"
          data-testid="search-input-supporting"
        >
          {supportingText}
        </p>
      ) : null}
    </div>
  );
});
